import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import { usePendingWrite } from '@/lib/use-pending-write';

const displayNameSchema = z.object({
  displayName: z.string().min(1, 'Campo obbligatorio').max(255),
});
type DisplayNameFormValues = z.infer<typeof displayNameSchema>;

/**
 * The self-service display-name edit (FR-SP-4, FR-SP-5, FR-SP-6, FR-SP-8; AC-PA-8, AC-PA-9,
 * AC-PA-10) — the ONLY editable control on the whole profile section (Resolution 7). Calls
 * `PATCH /v1/profile` (SL-0022) with exactly one body field, `displayName`, and no header
 * parameters (the operation declares none) — the server's own `.strict()` zod schema
 * structurally rejects any other field, so no client-side control in this file could ever widen
 * the request shape even if one were added by mistake. `onUpdated` lets `ProfilePage` reflect the
 * accepted change in its own Identity card without a full profile refetch.
 *
 * UI refinement: rendered inline inside the Identity card (no separate card/heading), so the
 * display name is named once. Save is enabled only when the value actually changed.
 */
export function DisplayNameForm({
  currentDisplayName,
  onUpdated,
}: {
  currentDisplayName: string;
  onUpdated: (updated: { displayName: string }) => void;
}) {
  const { t } = useTranslation(['profile', 'common']);
  const [apiError, setApiError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<DisplayNameFormValues>({
    resolver: zodResolver(displayNameSchema),
    defaultValues: { displayName: currentDisplayName },
  });
  const { pending, run } = usePendingWrite();

  const onSubmit = async (values: DisplayNameFormValues) => {
    setApiError(null);
    setSaved(false);
    const { data, error, response } = await apiClient.PATCH('/v1/profile', {
      body: { displayName: values.displayName },
    });

    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('displayName.invalidRequest'));
      return;
    }

    reset({ displayName: data.displayName });
    setSaved(true);
    onUpdated({ displayName: data.displayName });
    toast.success(t('profile:displayName.savedToast'));
  };

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
    >
      <div className="space-y-2">
        <label className="field-label" htmlFor="profile-display-name">
          {t('displayName.label')}
        </label>
        <Input
          id="profile-display-name"
          className="max-w-md"
          {...register('displayName', { onChange: () => setSaved(false) })}
        />
        {errors.displayName && (
          <p className="text-destructive text-sm">{errors.displayName.message}</p>
        )}
        {apiError && <p className="text-destructive text-sm">{apiError}</p>}
        {saved && <p className="text-success text-sm">{t('displayName.saved')}</p>}
      </div>
      <Button type="submit" size="sm" disabled={!isDirty || pending}>
        {t('common:save')}
      </Button>
    </form>
  );
}
